import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface LivePriceData {
  price: number;
  change24h: number;
  change7d?: number;
  marketCap?: number;
}

interface UseLivePricesReturn {
  prices: Map<string, LivePriceData>;
  isLoading: boolean;
  lastUpdated: Date | null;
}

export function useLivePrices(symbols: string[] = []): UseLivePricesReturn {
  const [prices, setPrices] = useState<Map<string, LivePriceData>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const cacheRef = useRef<{ data: Map<string, LivePriceData>; fetchedAt: number } | null>(null);

  const fetchPrices = useCallback(async () => {
    // Use cache if less than 30s old
    if (cacheRef.current && Date.now() - cacheRef.current.fetchedAt < 30000) {
      setPrices(cacheRef.current.data);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('fetch-scored-markets');
      if (error) throw error;

      const tokenList = data?.tokens || [];
      const priceMap = new Map<string, LivePriceData>();

      tokenList.forEach((token: any) => {
        const sym = (token.symbol || '').toUpperCase();
        priceMap.set(sym, {
          price: token.price ?? 0,
          change24h: token.change_24h ?? 0,
          change7d: token.change_7d ?? undefined,
          marketCap: token.market_cap ?? undefined,
        });
      });

      cacheRef.current = { data: priceMap, fetchedAt: Date.now() };
      setPrices(priceMap);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('useLivePrices fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrices();
    const interval = setInterval(fetchPrices, 60_000);
    return () => clearInterval(interval);
  }, [fetchPrices]);

  return { prices, isLoading, lastUpdated };
}
